import { Box, Button, Card, CardContent, Container, Typography } from '@mui/material';
import { BusinessCenter, Groups, PersonAdd, Refresh } from '@mui/icons-material';
import { useMicrospecThemeLanguageContext } from '../Themes';
import { useOrganizationContext } from './OrganizationContext';

/**
 * NoMembershipPage Component
 * Displayed when user is in a organization-context-aware dashboard but has no organization/branch memberships
 * 
 * Features:
 * - Clear messaging about lack of organization membership
 * - Helpful guidance on next steps
 * - Refresh button to check for new memberships
 * - Modern, professional design with animations
 */
const NoMembershipPage = () => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { refresh, loading } = useOrganizationContext();

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 600,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(40, 40, 40, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.95) 100%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: (theme) => theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(0, 0, 0, 0.12)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 12px 40px rgba(0, 0, 0, 0.5)'
                : '0 12px 40px rgba(0, 0, 0, 0.16)',
            }
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Icon Section */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(21, 101, 192, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(21, 101, 192, 0.1) 100%)',
                  border: (theme) => theme.palette.mode === 'dark'
                    ? '2px solid rgba(33, 150, 243, 0.3)'
                    : '2px solid rgba(33, 150, 243, 0.2)',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                      opacity: 0.8,
                    },
                  },
                }}
              >
                <BusinessCenter
                  sx={{
                    fontSize: 48,
                    color: 'primary.main',
                  }}
                />
              </Box>
            </Box>

            {/* Title */}
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#1a1a1a',
                mb: 2,
              }}
            >
              {localizationProvider.translateByType({
                type: 'Internal',
                text: 'noOrganizationMembership'
              })}
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{
                mb: 4,
                lineHeight: 1.7,
                fontSize: '1rem',
              }}
            >
              {localizationProvider.translateByType({
                type: 'Internal',
                text: 'noOrganizationMembershipDescription'
              })}
            </Typography>

            {/* Action Steps */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#1a1a1a',
                }}
              >
                {localizationProvider.translateByType({
                  type: 'Internal',
                  text: 'nextSteps'
                })}
              </Typography>

              {/* Step 1 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 2,
                  p: 2,
                  borderRadius: '8px',
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)',
                  transition: 'background 0.2s ease',
                  '&:hover': {
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <PersonAdd
                  sx={{
                    mr: 2,
                    color: 'primary.main',
                    fontSize: 28,
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {localizationProvider.translateByType({
                      type: 'Internal',
                      text: 'contactOrganizationAdmin'
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {localizationProvider.translateByType({
                      type: 'Internal',
                      text: 'contactOrganizationAdminDescription'
                    })}
                  </Typography>
                </Box>
              </Box>

              {/* Step 2 */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  p: 2,
                  borderRadius: '8px',
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.03)'
                    : 'rgba(0, 0, 0, 0.02)',
                  transition: 'background 0.2s ease',
                  '&:hover': {
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <Groups
                  sx={{
                    mr: 2,
                    color: 'primary.main',
                    fontSize: 28,
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {localizationProvider.translateByType({
                      type: 'Internal',
                      text: 'waitForInvitation'
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {localizationProvider.translateByType({
                      type: 'Internal',
                      text: 'waitForInvitationDescription'
                    })}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Refresh Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Refresh />}
                onClick={refresh}
                disabled={loading}
                sx={{
                  borderRadius: '8px',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)'
                    : 'linear-gradient(135deg, #42A5F5 0%, #2196F3 100%)',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)'
                      : 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(33, 150, 243, 0.3)'
                      : 'rgba(33, 150, 243, 0.2)',
                    color: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.5)'
                      : 'rgba(0, 0, 0, 0.3)',
                  }
                }}
              >
                {localizationProvider.translateByType({
                  type: 'Internal',
                  text: loading ? 'checking' : 'checkForMemberships'
                })}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default NoMembershipPage;
